/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { child, get, getDatabase, ref, set } from "firebase/database";
import { getDownloadURL, uploadString } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, realtime, storage, functions } from "../../../firebaseConfig";

import { useAuth } from "../auth/AuthProvider";
// import SalesforceAuth from "../auth/SalesforceAuth";

import NavBar from "../NavBar";
import StatDS from "./StatDS";
import AddElement from "../AddElement";
import { makeId } from "../../utils/id";
import AddSlide from "../AddSlide";
import DeleteSlide from "../DeleteSlide";
import JSON5 from "json5";
// import { marked } from "marked";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import TableToolbar from "../Flexmonster/TableToolbar";
import EditorsContainer from "../TipTap/TiptapNew";
import Papa from "papaparse";
import { Loader } from "./../Loader/Loader";
import ComponentToolbar from "../ComponentToolbar/ComponentToolbar";
import CollabLayer from "../Collab/CollabLayer";
import Mermaid from "../Mermaid/Mermaid";
import { initializeScroll, scrollToElement } from "../../utils/scroll";
import { useMergeLink } from "@mergeapi/react-merge-link";
// import ReactGoogleSlides from "react-google-slides";

// import Highcharts from "highcharts";
// import HighchartsReact from "highcharts-react-official";
// import HighchartsMap from "highcharts/modules/map";
// import GeoMapComponent from "../Highcharts/GeoMapComponent";
// import HighchartsComponent from "../Highcharts/HighchartsComponent";

import "./ViewPage.css";
import "../Highcharts/highcharts.css";
import "../AddElement.css";

// import { useTour } from '@reactour/tour'

function ViewPage() {
  const { currentUser } = useAuth(); // Get the current user from context
  const { viewId } = useParams();
  const location = useLocation();
  const [viewData, setViewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  // const [contentBlocks, setContentBlocks] = useState([]);
  // const [visualizationConfig, setVisualizationConfig] = useState(null);
  const [lastComponentId, setLastComponentId] = useState(0);

  const [scrollTo, setScrollTo] = useState(null);
  const [isPresentationMode, setIsPresentationMode] = useState(null);
  const [isSlideNavOpen, setIsSlideNavOpen] = useState(false);
  const [selectedSlide, setSelectedSlideState] = useState(null);
  const [highchartsData, setHighchartsData] = useState({});
  const [editingComponentId, setEditingComponentId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [fakeUsersVisible, setFakeUsersVisible] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.metaKey && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setFakeUsersVisible(prevVisible => !prevVisible);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleFlexmonsterDataUpdate = (componentId, data) => {
    setHighchartsData(prev => ({...prev, [componentId]: data}));
  };

  const [, setIsGAuthOpen] = useState(false); // New state variable for the modal


  const COLLAB_EDITING_TIMEOUT = 10;

  const pivotRef = useRef(null);
  const flexmonsterRef = useRef(null);

  const navBarRef = useRef(null);
  const hasRunAgentMode = useRef(false);
  // const [isGoogleSignedIn, setIsGoogleSignedIn] = useState(false); // State to track Google Auth status

  const googleAuthRef = useRef(null); // Ref for GoogleSheetsAuth
  const tableToolbarRefs = useRef(new Map());

  //// -- CREATE COLLABORATORS -- ////
  const createCollaborators = useCallback(
    async (c, createdBy) => {
      const collaborators = [];

      // Add current user to collaborators list
      // console.log("createCollaborators", c, currentUser);
      collaborators.push({
        isCurrentUser: true,
        id: currentUser.uid,
        name: currentUser.displayName,
      });

      // Add created by user to collaborators list if not the current user
      // console.log("createdBy", createdBy, "currentUser", currentUser.uid);
      if (createdBy !== currentUser.uid) {
        const docRef = doc(db, "users", createdBy);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          // console.log("createdBy user data", docSnap.data());
          const collaboratorData = docSnap.data();
          collaborators.push({
            id: createdBy,
            name: collaboratorData.name ? collaboratorData.name : collaboratorData.firstName + " " + collaboratorData.lastName,
            firstName: collaboratorData.firstName,
            lastName: collaboratorData.lastName,
          });
        }
      }

      await Promise.all(
        c.map(async (collaborator) => {
          // Get user doc for collaborator
          const docRef = doc(db, "users", collaborator.id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists() && currentUser.uid !== collaborator.id) {
            const collaboratorData = docSnap.data();
            // console.log("collaborator user data", collaboratorData);
            collaborators.push({
              id: collaborator.id,
              name: collaboratorData.name ? collaboratorData.name : collaboratorData.firstName + " " + collaboratorData.lastName,
              firstName: collaboratorData.firstName,
              lastName: collaboratorData.lastName,
            });
          } else {
            return;
          }
        })
      );

      return collaborators;
    },
    [currentUser.uid, currentUser.displayName]
  );

  const fetchViewData = useCallback(
    async (componentId, activeUser) => {
      setIsLoading(true);
      const docRef = doc(db, "views", viewId);
      try {
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          await updateDoc(docRef, {
            editedAt: serverTimestamp(),
          });
          const collaborators = docSnap.data().collaborators ? await createCollaborators(docSnap.data().collaborators, docSnap.data().createdBy) : [];

          // If fetching view of a component update, only update the component in viewData
          if (componentId) {
            const updatedContents = docSnap.data().contents.map((c) => {
              if (c.id === componentId) {
                const updatedComponent = c;
                // Add the activeUser from the component in collabLayer to the component
                if (activeUser) {
                  updatedComponent.activeUser = activeUser;
                  if (activeUser.uid !== currentUser.uid) updatedComponent.isLocked = true;
                }
                return c;
              }
              return c;
            });

            setViewData({
              ...viewData,
              contents: updatedContents,
              slides: docSnap.data().slides ?? [],
              collaborators: collaborators,
              editedAt: docSnap.data().editedAt,
            });
            setIsLoading(false);
            return;
          }

          // Update the full view data
          const updatedContents = docSnap.data().contents ?? [];
          setViewData({
            ...viewData,
            contents: updatedContents,
            slides: docSnap.data().slides ?? [],
            collaborators: collaborators,
            fileData: docSnap.data().fileData,
            editedAt: docSnap.data().editedAt,
          });
        } else {
          console.log("No such view exists!");
        }
      } catch (error) {
        console.error("Error fetching view data:", error);
        setViewData({
          contents: [],
          error: {
            code: error.code || "fetch-error",
            message:
              error.code === "permission-denied" ? "You do not have permission to view this content." : "There was an error fetching the view.",
          },
        });
      }

      setIsLoading(false);
    },
    [createCollaborators, currentUser.uid, viewData, viewId, setViewData]
  );

  if (!viewData && !isLoading) fetchViewData();

  const handleGAuthModalToggle = (isOpen) => {
    // console.log("G Auth modal toggle in ViewPage:", isOpen);
    setIsGAuthOpen(isOpen); // Update the state in ViewPage
  };

  // console.log(currentUser);

  // const handleContentSave = useCallback(
  //   (contentId, newContent) => {
  //     console.log("handleContentSave", contentId, newContent);
  //     updateContent(contentId, newContent)
  //       .then(() => console.log("Content updated successfully!"))
  //       .catch((error) => console.error("Failed to update content", error));
  //   },
  //   [updateContent]
  // );

  const saveComponentsToFirestore = useCallback(
    async (newComponents, slideUpdates) => {
      const docRef = doc(db, "views", viewId);
      try {
        // 2. Get Existing Document Data
        // console.log(`Attempting to get document: views/${viewId}`);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          // console.error(`saveComponentsToFirestore Error: Document views/${viewId} does not exist.`);
          return false; // Indicate failure: document not found
        }

        // 3. Prepare Updated Data
        const data = docSnap.data();
        const existingComponents = data.contents || [];
        const finalComponents = [...existingComponents, ...newComponents];
        // console.log("Firestore save: Preparing to update with final components:", finalComponents); // Use JSON.stringify if needed for deep inspection

        // Prepare the update payload
        const updatePayload = {
          contents: finalComponents,
          editedAt: new Date(),
          editedBy: currentUser.uid,
        };

        // If slideUpdates are provided, merge them into the payload
        if (slideUpdates !== undefined) {
          // Check if the second argument was passed
          if (Array.isArray(slideUpdates)) {
            updatePayload.slides = slideUpdates; // Update the slides field
          } else {
            // console.warn("saveComponentsToFirestore Warning: slideUpdates argument was provided but is not an array.", slideUpdates);
          }
        }
        // 4. Attempt Firestore Update
        // console.log(`Attempting to update document: views/${viewId}`);
        await updateDoc(docRef, updatePayload);

        // 5. Verification / Success Handling
        console.log(`Firestore update successful for views/${viewId}.`);
        return true; // Indicate success
      } catch (error) {
        // 6. Error Handling
        // console.error(`saveComponentsToFirestore Error updating views/${viewId}:`, error);
        // Log specific details if available
        if (error.code) {
          // console.error(`Firestore Error Code: ${error.code}`);
        }
        if (error.message) {
          // console.error(`Firestore Error Message: ${error.message}`);
        }
        // Consider adding user-facing error feedback here if appropriate
        return false; // Indicate failure
      }
    },
    [viewId, currentUser.uid]
  );

  const handleComponentEditing = useCallback(
    async (componentId) => {
      // console.log("handleComponentEditing", componentId);
      // Set the realtime database component active user to the current user
      if (componentId) {
        const componentActiveUserPath = `collabLayer/views/${viewId}/components/${componentId}/activeUser`;
        const componentActiveUserRef = ref(realtime, componentActiveUserPath);
        // Check if the component is already being edited by another user and has not been updated in the timeout window
        try {
          const dbRef = ref(getDatabase());
          const componentData = await get(child(dbRef, componentActiveUserPath));
          if (componentData && componentData.uid !== currentUser.uid && Date.now() - componentData.lastUpdated < COLLAB_EDITING_TIMEOUT) {
            // console.log("Component is being edited by another user");
            return;
          }
        } catch (error) {
          // console.error("Error fetching component data:", error);
        }

        // console.log("Component is being edited by the current user, set the collabLayer active user");
        try {
          await set(componentActiveUserRef, { uid: currentUser.uid, lastUpdated: Date.now() });
        } catch (error) {
          // console.error("Error setting active user:", error);
        }

        // Set timeout to clear the active user if the user does not update the component
        setTimeout(async () => {
          // console.log("Clearing active user");
          try {
            await set(componentActiveUserRef, null);
          } catch (error) {
            // console.error("Error clearing active user:", error);
          }
        }, COLLAB_EDITING_TIMEOUT);
      }
    },
    [viewId, currentUser.uid]
  );

  const handleCreateVisualization = useCallback(
    async (config) => {
      // console.log("handleCreateVisualization config", config);

      // Create a new slide and add component to it
      const docRef = doc(db, "views", viewId); // Ensure you have a valid document reference
      const newSlideID = makeId(10);
      let slidesArray = [];

      try {
        const docSnap = await getDoc(docRef); // Get the current document snapshot
        if (docSnap.exists()) {
          // If document exists, retrieve the existing details
          const data = docSnap.data();
          slidesArray = data.slides ?? [];
        } else {
          console.error("Error saving slide to Firestore. View not found:", viewId);
          return false;
        }

        // console.log("initial slidesArray", slidesArray);

        // Find the current slide components
        const slideComponents = [];
        console.log("slideComponents", slideComponents);

        // Add new component to the current slide
        slideComponents.push(config.newComponentID);

        // Update the slidesArray with the updated slide
        slidesArray.push({ id: newSlideID, components: slideComponents });
        console.log("updated slidesArray", slidesArray);

        // Set the document with updated details
        await updateDoc(docRef, { slides: slidesArray });
        console.log("Slides updated successfully in Firestore.");

        // Fetch the view data with the updated slide and component
        await fetchViewData(config.newComponentID);

        // Set the realtime database component active user to the current user
        await handleComponentEditing(config.newComponentID);
      } catch (error) {
        console.error("Error updating Firestore:", error);
      }

      if (config && config.type === "csv") {
        const csvData = config.data;
        if (pivotRef.current) {
          flexmonsterRef.current = pivotRef.current.flexmonster;
          flexmonsterRef.setReport(
            {
              dataSource: {
                type: "csv",
                data: csvData,
              },
            },
            []
          );
        }
      } else {
        // setVisualizationConfig(config); // Does nothing
      }
    },
    [viewId, fetchViewData, handleComponentEditing]
  );

  // Function to pass to NavBar/GeminiComponent to request Google sign-in
  const handleRequestGoogleSignIn = useCallback(() => {
    if (googleAuthRef.current && googleAuthRef.current.handleAuthClick) {
      console.log("ViewPage: Requesting Google Sign-In via GoogleSheetsAuth ref...");
      googleAuthRef.current.handleAuthClick();
    } else {
      console.error("ViewPage: GoogleSheetsAuth ref not available or handleAuthClick not exposed.");
    }
  }, [googleAuthRef]);

  // Function to pass to NavBar/GeminiComponent to create a Google Doc
  const handleCreateGoogleDocApiCall = useCallback(
    async (title, body) => {
      if (googleAuthRef.current && googleAuthRef.current.createDocumentWithContent) {
        console.log("ViewPage: Calling createDocumentWithContent via GoogleSheetsAuth ref...");
        try {
          return await googleAuthRef.current.createDocumentWithContent(title, body);
        } catch (error) {
          console.error("ViewPage: Error calling createDocumentWithContent:", error);
          throw error; // Re-throw to be handled by GeminiComponent
        }
      } else {
        console.error("ViewPage: GoogleSheetsAuth ref not available or createDocumentWithContent not exposed.");
        throw new Error("Google Docs creation service not available.");
      }
    },
    [googleAuthRef]
  );

  const handleCreateTextEditor = useCallback(
    async (textContent) => {
      console.log("handleCreateTextEditor called with content length:", textContent?.length);

      // 1. Validate Input
      if (typeof textContent !== "string") {
        // console.error("handleCreateTextEditor Error: textContent must be a string.");
        return false; // Indicate failure
      }
      if (!viewId) {
        // console.error("handleCreateTextEditor Error: viewId is missing.");
        return false;
      }

      // 2. Generate ID and Create Component Object
      const newComponentId = makeId(10); // Use your existing ID generator
      const newTextComponent = {
        id: newComponentId,
        type: "text-editor",
        content: textContent, // Store the raw text content
        cards: 3, // Default size, adjust if needed
      };
      // console.log("Creating new text component:", newTextComponent);

      // 3. Determine Target Slide and Prepare Slide Updates
      let updatedSlidesArray = [];
      const docRef = doc(db, "views", viewId);
      try {
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          // console.error(`handleCreateTextEditor Error: Document views/${viewId} does not exist.`);
          return false;
        }

        const data = docSnap.data();
        const currentSlides = data.slides || [];

        if (currentSlides.length === 0) {
          // No slides exist, create the first one
          const newSlideId = makeId(10);
          updatedSlidesArray = [{ id: newSlideId, components: [newComponentId] }];
          // console.log("No existing slides, creating new slide:", updatedSlidesArray[0]);
        } else {
          // Add to the last existing slide
          const lastSlideIndex = currentSlides.length - 1;
          const lastSlide = { ...currentSlides[lastSlideIndex] }; // Shallow copy
          lastSlide.components = [...(lastSlide.components || []), newComponentId]; // Add new component ID
          updatedSlidesArray = [...currentSlides]; // Copy existing slides
          updatedSlidesArray[lastSlideIndex] = lastSlide; // Replace last slide with updated one
          // console.log(`Adding component ${newComponentId} to last slide ${lastSlide.id}`);
        }

        // 4. Save the new component and the updated slides array to Firestore
        // We pass the new component in an array, and the updated slides array
        const success = await saveComponentsToFirestore([newTextComponent], updatedSlidesArray);

        if (success) {
          // console.log("handleCreateTextEditor: Firestore updated successfully.");
          // 5. Refresh UI Data (optional but recommended)
          // You might want to call fetchViewData or update local state directly
          await fetchViewData(); // Re-fetch all data to ensure UI consistency
          // Optionally scroll to the new component/slide
          // setScrollTo(newComponentId); // Or the slide ID if preferred
          return true; // Indicate success
        } else {
          // console.error("handleCreateTextEditor: Failed to save updates to Firestore.");
          return false; // Indicate failure
        }
      } catch (error) {
        // console.error("handleCreateTextEditor Error getting/updating slides:", error);
        return false; // Indicate failure
      }
    },
    [viewId, saveComponentsToFirestore, fetchViewData]
  );

  const uploadJsonToStorage = async (jsonContent, newId) => {
    const storageRef = ref(storage, `csv-json/${newId}.json`);
    await uploadString(
      storageRef,
      JSON.stringify(jsonContent),
      "raw",
      {
        contentType: "application/json",
      },
      []
    );
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  const handleGeminiData = useCallback(
    async (data) => {
      // console.log("handleGeminiData", data);
      const newRowId = lastComponentId + 1;

      if (data) {
        if (data.csvContent) {
          const parsedData = Papa.parse(data.csvContent, { header: true });
          const jsonContent = parsedData.data;

          const newId = `component-${newRowId}B`;

          const downloadURL = await uploadJsonToStorage(jsonContent, newId);

          const newComponent = {
            cards: 3,
            type: "data-visualization",
            id: newId,
            content: JSON.stringify({ url: downloadURL, type: "json" }),
          };

          await saveComponentsToFirestore([newComponent], newRowId);

          setLastComponentId(newRowId);
        } else if (data.markdownContent) {
          // const newContent = marked(data.markdownContent);
          // setContentBlocks((prevBlocks) => [...prevBlocks, newContent]);
        } else if (data.tableContent) {
          try {
            const jsonData = await fetch(apiUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ data: data.tableContent }),
            }).then(
              (response) => {
                if (!response.ok) {
                  throw new Error(`DS - Network response was not ok, status: ${response.status}`);
                }
                return response.json();
              },
              [viewId, realtime, currentUser.uid]
            );

            await handleCreateVisualization(jsonData);
            // await handleAddFlexmonsterComponent(jsonData);
          } catch (error) {
            // console.error("DS - Error converting table data:", error);
          }
        } else if (data.text) {
          const newTextContent = data.text;
          // setContentBlocks((prevBlocks) => [...prevBlocks, newTextContent]);
          // console.log("markdown contenthandleGeminiData", data);

          const newTextComponent = {
            cards: 3,
            type: "text-editor",
            id: makeId(10),
            content: newTextContent,
          };
          await handleCreateTextEditor(newTextContent);
          await saveComponentsToFirestore([newTextComponent], newRowId);
        } else {
          // console.log("DS - Received data does not include suitable content");
        }
      }
    },
    [currentUser.uid, handleCreateTextEditor, handleCreateVisualization, lastComponentId, saveComponentsToFirestore, viewId]
  );

  // Prevent shift+arrow scrolling when a text field is focused
  useEffect(() => {
    const handleKeyDown = (e) => {
      const { tagName, isContentEditable } = e.target;
      const isTextField = tagName === "INPUT" || tagName === "TEXTAREA" || isContentEditable;

      if (isTextField && e.shiftKey && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Scrolling
  useEffect(() => {
    if (scrollTo) {
      scrollToElement(scrollTo);
      setScrollTo(null);
    }
  }, [scrollTo]);

  useEffect(() => {
    const cleanup = initializeScroll(viewData, isPresentationMode);
    return cleanup;
  }, [viewData, isPresentationMode]);

  const handleElementUpdate = (elementType, componentId, action) => {
    // Update the element based on the elementType and id
    if (!isLoading) {
      switch (elementType) {
        case "slide":
          fetchViewData();
          break;
        case "component":
          fetchViewData(componentId);
          break;
        default:
          fetchViewData();
      }
    }

    // Update the view last updated date in collabLayer
    if (action === "delete" || action === "add") {
      const date = Date.now();
      setLastUpdated(date);
      set(ref(realtime, `collabLayer/views/${viewId}/settings/lastUpdated`), date);
    }
  };

  const handleCollabLayerUpdate = (type = "last-updated", data) => {
    // console.log("handleCollabLayerUpdate", type, data, `isLoading:${isLoading}`);
    const updatedViewData = viewData;
    let updatedComponentIndex;

    switch (type) {
      case "last-updated":
        if (!isLoading) fetchViewData();
        break;
      case "component-editing":
        // Find the updated component in the viewData and update the active user data for the component
        updatedComponentIndex = updatedViewData?.contents?.findIndex((c) => c.id === data?.componentId);
        if (updatedComponentIndex !== -1) {
          // if (data.activeUser) {
          //   updatedViewData.contents[updatedComponentIndex].activeUser = data.activeUser;
          // } else {
          //   delete updatedViewData.contents[updatedComponentIndex].activeUser;
          // }
          // console.log("update the view data with the active user", updatedViewData.contents[updatedComponentIndex]);
          // setViewData(updatedViewData);

          if (data?.activeUser?.uid === currentUser.uid) {
            // console.log(
            //   "handleCollabLayerUpdate: Component is being edited by the current user",
            //   `currentUser:${currentUser.uid}`,
            //   `activeUser:${data?.activeUser?.uid}`
            // );
            // updatedViewData.contents[updatedComponentIndex].isLocked = false;
          } else {
            // console.log(
            //   "handleCollabLayerUpdate: Component is being edited by another user",
            //   `currentUser:${currentUser.uid}`,
            //   `activeUser:${data?.activeUser?.uid}`
            // );
            // updatedViewData.contents[updatedComponentIndex].isLocked = true;

            // Find the updated component in the viewData and update the component
            // setViewData(updatedViewData);
            if (!isLoading) {
              setTimeout(() => {
                fetchViewData(data?.componentId, data.activeUser);
              }, 500);
            }
          }
        }
        break;
      default:
        break;
    }
  };


  const handleSlideCreated = useCallback(
    async (newSlideId) => {
      console.log("ViewPage: handleSlideCreated called with", newSlideId);
      const date = Date.now();
      setLastUpdated(date);
      await fetchViewData();
      if (newSlideId) {
        setTimeout(() => {
          setScrollTo(newSlideId);
        }, 600);
      }
    },
    [fetchViewData]
  );

  const handlePresentationModeChange = (value) => {
    setIsPresentationMode(value);
  };

  const handleAddSlide = () => {
    setIsSlideNavOpen(!isSlideNavOpen);
  };

  const setSelectedSlide = (slideId) => {
    setSelectedSlideState(slideId);
    setScrollTo(slideId);
  };

  // Set up the slides and group the components into rows based on cards
  const slides = useMemo(() => {
    const slidesArray = [];
    if (viewData?.slides?.length > 0) {
      viewData.slides.forEach((slide) => {
        let currentRowLength = 0;
        const slideRows = [];
        let row = [];
        if (slide.components && slide.components.length > 0) {
          slide.components.forEach((slideComponent, index) => {
            const component = viewData.contents?.find((c) => c.id === slideComponent);
            if (!component) return; // Skip if component not found

            // Add the activeUser from the component in collabLayer to the component
            if (component && viewData.collaborators && component) {
              // Set the component to locked if it is being edited by another user
              component.isLocked = component.activeUser && component.activeUser?.uid !== currentUser.uid;
            }

            if (currentRowLength < 3 && component.cards + currentRowLength <= 3) {
              row.push(component);
            } else {
              slideRows.push({ contents: row });
              row = [];
              currentRowLength = 0;
              row.push(component);
            }
            if (slide.components.length === index + 1) {
              slideRows.push({ contents: row });
            }
            currentRowLength += component.cards;
          });
        }
        slidesArray.push({ id: slide.id, rows: slideRows });
      });
    }
    return slidesArray;
  }, [currentUser?.uid, viewData?.collaborators, viewData?.contents, viewData?.slides]);

  // const { setIsOpen } = useTour()


  // -------  RETURN   ------- \
  return (
    <div>
      <NavBar
        ref={navBarRef}
        classname="navbar"
        onSendData={handleGeminiData}
        onCreateVisualization={handleCreateVisualization}
        onSlideCreated={handleSlideCreated}
        viewId={viewId}
        onPresentationModeChange={handlePresentationModeChange}
        components={viewData?.contents || []}
        userID={currentUser}
        tableToolbarRef={tableToolbarRefs}
        // isUserGoogleSignedIn={isGoogleSignedIn}
        onRequestGoogleSignIn={handleRequestGoogleSignIn}
        createGoogleDocApiCall={handleCreateGoogleDocApiCall}
        slideData={viewData?.slides || []}
        isExporting={isExporting}
        setIsExporting={setIsExporting}
        // -- SAVE TO TEMPLATE BUTTON
        // pageTitle={<button className="build-element">Save this template</button>}
      />

      {isLoading && <Loader isLoading={isLoading} />}
      {/* <Loader isLoading={isLoading} /> */}

      <div className={`view-page${isPresentationMode ? " presentation-mode" : ""}`}>
        {viewData?.error?.message && (
          <div className="error-message">
            <svg className="error-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z"></path>
            </svg>
            <p>{viewData.error.message}</p>
          </div>
        )}

        {(viewData?.collaborators?.length > 0 || fakeUsersVisible) && (
          <CollabLayer
            key="CollabLayer"
            viewId={viewId}
            lastUpdated={lastUpdated}
            setLastUpdated={setLastUpdated}
            isLoading={isLoading}
            currentUser={currentUser}
            collaborators={viewData?.collaborators || []}
            components={viewData?.contents?.map((c) => c.id) || []}
            onUpdate={handleCollabLayerUpdate}
            fakeUsersVisible={fakeUsersVisible}
          />
        )}


        <div className="slides-container" style={{ overflowY: isPresentationMode ? "visible" : "auto" }}>
          {slides.map((slide) => (
            <div key={slide.id} id={slide.id} className={slide.rows.length > 0 ? "slide" : "slide empty"}>
              
              {slide.rows.length > 0 ? (

                slide.rows.map((row, rowIndex) => (
                  <div key={`${slide.id}-${rowIndex}`} className="dashboard-row">
                    {row.contents.map((component) => {
                      let configJson;
                      const isEditing = editingComponentId === component.id;
                      const style = {
                        ...(component.isLocked
                          ? { borderColor: component.activeUser.color, borderStyle: "solid", borderWidth: "1px" }
                          : { borderColor: "var(--Grey2)", borderStyle: "solid", borderWidth: "1px" }),
                        zIndex: isEditing ? 100 : 'auto',
                      };

                      switch (component.type) {
                        case "text-editor":
                          return (
                            <ErrorBoundary key={`ErrorBoundary-${component.id}`}>
                              <div
                                id={component.id}
                                key={`${slide.id}-${rowIndex}-${component.id}-${component.activeUser}`}
                                className={`column tcolumn-${component.cards}`}
                                style={style}
                              >
                                <EditorsContainer
                                  key={`EditorsContainer-${component.id}`}
                                  id={component.id}
                                  content={component.content}
                                  onEditing={() => {
                                    handleComponentEditing(component.id);
                                  }}
                                  editable={!component.isLocked && !isPresentationMode}
                                  onFocus={() => setEditingComponentId(component.id)}
                                  onBlur={() => setEditingComponentId(null)}
                                />
                                <ComponentToolbar
                                  key={`ComponentToolbar-${component.id}`}
                                  viewId={viewId}
                                  slides={viewData.slides}
                                  component={component}
                                  currentUser={currentUser}
                                  onUpdate={(action) => {
                                    handleElementUpdate("component", component.id, action);
                                  }}
                                />
                              </div>
                            </ErrorBoundary>
                          );
                        case "data-visualization":
                          configJson = JSON5.parse(component.content);

                          return (
                            <ErrorBoundary key={`ErrorBoundary-${component.id}`}>
                              <div
                                id={component.id}
                                key={`${slide.id}-${rowIndex}-${component.id}-${component.activeUser}`}
                                className={`column column-${component.cards} flexmonster-container`}
                                style={style}
                              >
                                <TableToolbar
                                  ref={el => tableToolbarRefs.current.set(component.id, el)}
                                  key={`TableToolbar-${component.id}`}
                                  viewId={viewId}
                                  componentId={component.id}
                                  config={configJson}
                                  // data={configJson.dataSource?.type === "json" ? component.content : null}
                                  isPresentationMode={isPresentationMode}
                                  // onFirestoreUpdated={handleElementUpdate}
                                  onEditing={() => {
                                    handleComponentEditing("component", component.id);
                                  }}
                                  user={currentUser}
                                  onGAuthModalToggle={handleGAuthModalToggle} // Pass the G Auth callback
                                  onDataUpdate={handleFlexmonsterDataUpdate}
                                />

                                {/* <div  id="highchartsContainer" style={{position: "absolute", top: "140px"}}> */}

                                    {/* {console.log("configJson :"+configJson)};*/}
                                    {/* <HighchartsComponent
                                    key={`HighchartsComponent-${component.id}`}
                                    chartData={highchartsData[component.id]}
                                    />  */}
                                    {/* <GoogleSheetsProvider> */}
                                    {/* {console.log("pivotRef", flexmonster)} */}
                                    {/* </GoogleSheetsProvider> */}

                                {/* </div> */}

                                <ComponentToolbar
                                  key={`ComponentToolbar-${component.id}`}
                                  viewId={viewId}
                                  slides={viewData.slides}
                                  component={component}
                                  currentUser={currentUser}
                                  onUpdate={(action) => {
                                    handleElementUpdate("component", component.id, action);
                                  }}
                                />
                              </div>
                            </ErrorBoundary>
                          );
                          case "diagram":
                          return (
                            <ErrorBoundary key={`ErrorBoundary-${component.id}`}>
                              <div id={component.id}
                                key={`${slide.id}-${rowIndex}-${component.id}-${component.activeUser}`}
                                className={`column column-${component.cards} diagram-container`}
                                style={style}
                              >
                                <Mermaid
                                  id={component.id}
                                  content={component.content}
                                  onEditing={() => handleComponentEditing(component.id)}
                                  editable={!component.isLocked && !isPresentationMode}
                                  onFocus={() => setEditingComponentId(component.id)}
                                  onBlur={() => setEditingComponentId(null)}
                                  viewId={viewId}
                                />
                                <ComponentToolbar
                                  key={`ComponentToolbar-${component.id}`}
                                  viewId={viewId}
                                  slides={viewData.slides}
                                  component={component}
                                  currentUser={currentUser}
                                  onUpdate={(action) => {
                                    handleElementUpdate("component", component.id, action);
                                  }}
                                />
                              </div>
                            </ErrorBoundary>
                          );
                          case "data-sig":
                          return (
                            <ErrorBoundary key={`ErrorBoundary-${component.id}`}>
                              <div id={component.id}
                                key={`${slide.id}-${rowIndex}-${component.id}-${component.activeUser}`}
                                className={`column column-${component.cards} flexmonster-container`}
                                style={style}
                              >
                                <StatDS component={component} viewId={viewId} currentUser={currentUser} />

                                <ComponentToolbar
                                  key={`ComponentToolbar-${component.id}`}
                                  viewId={viewId}
                                  slides={viewData.slides}
                                  component={component}
                                  currentUser={currentUser}
                                  onUpdate={(action) => {
                                    handleElementUpdate("component", component.id, action);
                                  }}
                                />
                              </div>
                            </ErrorBoundary>
                          );
                        default:

                        // case "g-slides":
                        //   configJson = component.content;
                        //   return (
                        //     <ErrorBoundary key={`ErrorBoundary-${component.id}`}>
                        //       <div
                        //         key={`${slide.id}-${rowIndex}-${component.id}-${component.activeUser}`}
                        //         className={`column column-${component.cards} flexmonster-container`}
                        //         style={style}
                        //       >
                        //         {/* {"component:" + component.id + " activeUser:" + component.activeUser?.uid} */}
                        //         <ReactGoogleSlides
                        //           width="100%"
                        //           height="800"
                        //           slidesLink={configJson}
                        //           slideDuration={5}
                        //           position={1}
                        //           showControls
                        //           loop
                        //         />
                        //         <ComponentToolbar
                        //           key={`ComponentToolbar-${component.id}`}
                        //           viewId={viewId}
                        //           slides={viewData.slides}
                        //           component={component}
                        //           currentUser={currentUser}
                        //           onUpdate={(action) => {
                        //             handleElementUpdate("component", component.id, action);
                        //           }}
                        //         />
                        //       </div>
                        //     </ErrorBoundary>
                        //   );
                        // case "geo-map":
                        //   configJson = component.content;
                        //   return (
                        //     <ErrorBoundary key={`ErrorBoundary-${component.id}`}>
                        //       <div
                        //         id={component.id}
                        //         key={`${slide.id}-${rowIndex}-${component.id}-${component.activeUser}`}
                        //         className={`column column-${component.cards} flexmonster-container`}
                        //         style={style}
                        //       >
                        //         <GeoMapComponent />

                        //         <ComponentToolbar
                        //           key={`ComponentToolbar-${component.id}`}
                        //           viewId={viewId}
                        //           slides={viewData.slides}
                        //           component={component}
                        //           currentUser={currentUser}
                        //           onUpdate={(action) => {
                        //             handleElementUpdate("component", component.id, action);
                        //           }}
                        //         />
                        //       </div>
                        //     </ErrorBoundary>
                        //   );
                      }
                    })}
                    
                  </div>
                  
                ))
              ) : (
                <div className="empty-state">
                  {/* <div>Add content to your slide</div> */}
                <div className="slide-controls">
                <AddElement
                  defaultMenuOpen={true}
                  viewId={viewId}
                  slideId={slide.id}
                  onElementCreated={(componentId) => {
                    // console.log("Card created");
                    handleElementUpdate("component", componentId, "add");
                  }}
                />
                
                </div>
                </div>
              )}
              {slide.rows.length > 0 && (
              <div className="slide-controls">
                <AddElement
                  defaultMenuOpen={false}
                  viewId={viewId}
                  slideId={slide.id}
                  onElementCreated={(componentId) => {
                    console.log("Element created");
                    handleElementUpdate("component", componentId, "add");
                  }}
                />
              </div>
              )}

              {slide.rows.length === 0 ? (
                <DeleteSlide
                  viewId={viewId}
                  slideId={slide.id}
                  onSlideDeleted={() => {
                    handleElementUpdate("slide", slide.id, "delete");
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>

        <div
          className={`slide-nav-container ${isSlideNavOpen ? "open" : ""}`}
        >
          <div className="add-slide" style={{ display: "flex", alignItems: "center" }}>
            {slides.map((slide, index) => (
              <button style={{flexShrink:"0"}} key={slide.id} onClick={() => setSelectedSlide(slide.id)} className={`btn ${selectedSlide === slide.id ? "active" : ""}`}>
                Board {index + 1}
              </button>
            ))}

            <AddSlide viewId={viewId} onSlideCreated={handleSlideCreated} scrollSmooth />
          </div>

          <button onClick={handleAddSlide} className="open-menu-Btn-text" style={{ marginLeft: "16px" }}>
            Boards
          </button>

        </div>

    </div>
  );
}

export default ViewPage;
